import { Badge, Button, Input, Select, useToast } from '@orbind/ui'
import { useMemo, useRef, useState } from 'react'
import { useConfirm } from '../work-item/bits.tsx'
import {
  TIPOS_CAMPO,
  fieldImpact,
  useCatalog,
  useDeleteField,
  useSaveField,
  type CustomField,
  type CustomFieldGroup,
  type CustomFieldPlacement,
} from './custom-field-data.ts'
import './lista-de-campos.css'

/**
 * Lista completa do catálogo de campos, ocupando a área de trabalho inteira
 * (pedido 2026-08-30).
 *
 * O painel lateral do catálogo é bom para arrastar, mas ruim para enxergar o
 * conjunto: aqui cabem chave, tipo, pasta, obrigatoriedade e — o que o painel
 * não mostrava — ONDE cada campo é usado. Reordenar arrastando grava a ordem
 * que vale no painel e nas listas.
 */

const rotuloDe = (labels: Record<string, string>, fallback: string) =>
  labels['pt-BR'] || Object.values(labels)[0] || fallback

type Entidade = 'work_item' | 'company' | 'person'

const ENTIDADES: { value: Entidade; label: string; icon: string }[] = [
  { value: 'work_item', label: 'Item de Trabalho', icon: 'fas fa-tasks' },
  { value: 'company', label: 'Empresa', icon: 'fas fa-building' },
  { value: 'person', label: 'Pessoa', icon: 'fas fa-user' },
]

/** Colunas que dá para ordenar. */
type Coluna = 'nome' | 'chave' | 'tipo' | 'entidade' | 'pasta' | 'uso'

const COLUNAS: { col: Coluna; label: string }[] = [
  { col: 'nome', label: 'Campo' },
  { col: 'chave', label: 'Chave' },
  { col: 'tipo', label: 'Tipo' },
  { col: 'entidade', label: 'Entidade' },
  { col: 'pasta', label: 'Pasta' },
  { col: 'uso', label: 'Usado em' },
]

export function ListaDeCampos({
  onFechar,
  onEditarCampo,
}: {
  onFechar: () => void
  onEditarCampo: (campo: CustomField) => void
}) {
  const catalogo = useCatalog()
  const toast = useToast()
  const confirm = useConfirm()
  const salvar = useSaveField('lista')
  const apagar = useDeleteField('lista')

  const [busca, setBusca] = useState('')
  const [entidade, setEntidade] = useState<Entidade | 'all'>('all')
  /**
   * Ordenação por coluna (2026-08-31). `null` = ordem manual (a que o arraste
   * grava e que vale no painel do catálogo); clicar numa coluna passa por
   * crescente → decrescente → volta para a manual.
   */
  const [ord, setOrd] = useState<{ col: Coluna; dir: 'asc' | 'desc' } | null>(null)
  const ordenar = (col: Coluna) =>
    setOrd((o) => (o?.col !== col ? { col, dir: 'asc' } : o.dir === 'asc' ? { col, dir: 'desc' } : null))
  /** Filtros por pasta do catálogo, por tópico e por uso (2026-08-31). */
  const [fPasta, setFPasta] = useState('all')
  const [fTopico, setFTopico] = useState('all')
  const [fUso, setFUso] = useState('all')
  /**
   * O arraste inteiro vive FORA do estado do React, numa ref, e o destaque é
   * escrito direto no DOM.
   *
   * Redesenhar a tabela no meio de um arraste HTML5 faz o navegador cancelar e
   * reiniciar o arraste — e é isso que aparece como a lista piscando. Como
   * `dragover` dispara sem parar, qualquer `setState` aqui vira um pisca-pisca.
   * O estado só volta a mandar no fim, quando a nova ordem é gravada.
   */
  const tabelaRef = useRef<HTMLTableElement>(null)
  const arrasto = useRef<{ id: string; alvo: string | null; antes: boolean } | null>(null)

  /** Move o traço de destino para uma linha (ou apaga, com `linha` nula). */
  const marcarDestino = (linha: HTMLElement | null, antes: boolean) => {
    for (const tr of tabelaRef.current?.querySelectorAll('tbody tr') ?? []) {
      tr.classList.remove('lcf__linha--antes', 'lcf__linha--depois')
    }
    linha?.classList.add(antes ? 'lcf__linha--antes' : 'lcf__linha--depois')
  }

  const limparArrasto = () => {
    marcarDestino(null, true)
    for (const tr of tabelaRef.current?.querySelectorAll('tbody tr') ?? []) {
      tr.classList.remove('lcf__linha--indo')
    }
    tabelaRef.current?.classList.remove('lcf__tabela--arrastando')
    arrasto.current = null
  }

  const topicos: CustomFieldGroup[] = catalogo.data?.topics ?? []
  const pastas: CustomFieldGroup[] = catalogo.data?.folders ?? []
  const colocacoes: CustomFieldPlacement[] = catalogo.data?.placements ?? []

  const todos = useMemo(
    () => [...(catalogo.data?.work_item ?? []), ...(catalogo.data?.company ?? []), ...(catalogo.data?.person ?? [])],
    [catalogo.data],
  )

  /** Onde o campo aparece: os tópicos que o colocaram, e a barra de propriedades. */
  const usoDe = (f: CustomField) => {
    const minhas = colocacoes.filter((p) => p.fieldId === f.id)
    const nomes = minhas.map((p) => {
      if (p.groupId === null) return 'Barra de propriedades'
      const t = topicos.find((g) => g.id === p.groupId)
      return t ? rotuloDe(t.labels, 'Tópico') : 'Tópico removido'
    })
    // sem colocação mas com tópico no próprio campo: ainda conta como uso
    if (nomes.length === 0 && f.groupId) {
      const t = topicos.find((g) => g.id === f.groupId)
      if (t) nomes.push(rotuloDe(t.labels, 'Tópico'))
    }
    return [...new Set(nomes)]
  }

  /** Texto de uma coluna, usado tanto para exibir quanto para ordenar. */
  const valorDe = (f: CustomField, col: Coluna) => {
    switch (col) {
      case 'nome':
        return rotuloDe(f.labels, f.key)
      case 'chave':
        return f.key
      case 'tipo':
        return TIPOS_CAMPO.find((t) => t.value === f.type)?.label ?? f.type
      case 'entidade':
        return ENTIDADES.find((e) => e.value === f.entity)?.label ?? f.entity
      case 'pasta': {
        const pasta = pastas.find((p) => p.id === f.catalogGroupId)
        return pasta ? rotuloDe(pasta.labels, 'Pasta') : ''
      }
      case 'uso':
        return usoDe(f).join(', ')
    }
  }

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    const filtrada = todos
      .filter((f) => entidade === 'all' || f.entity === entidade)
      .filter((f) => fPasta === 'all' || (fPasta === 'none' ? !f.catalogGroupId : f.catalogGroupId === fPasta))
      .filter((f) => {
        if (fTopico === 'all') return true
        const ids = new Set(colocacoes.filter((p) => p.fieldId === f.id).map((p) => p.groupId))
        if (f.groupId) ids.add(f.groupId)
        return fTopico === 'none' ? ids.size === 0 || (ids.size === 1 && ids.has(null)) : ids.has(fTopico)
      })
      .filter((f) => {
        if (fUso === 'all') return true
        const usos = usoDe(f)
        if (fUso === 'none') return usos.length === 0
        if (fUso === 'any') return usos.length > 0
        return usos.includes('Barra de propriedades')
      })
      .filter(
        (f) =>
          termo === '' ||
          rotuloDe(f.labels, f.key).toLowerCase().includes(termo) ||
          f.key.toLowerCase().includes(termo) ||
          (TIPOS_CAMPO.find((t) => t.value === f.type)?.label ?? '').toLowerCase().includes(termo),
      )
    if (!ord) return filtrada.sort((a, b) => a.entity.localeCompare(b.entity) || a.sortOrder - b.sortOrder)
    const sinal = ord.dir === 'asc' ? 1 : -1
    return filtrada.sort((a, b) => {
      const va = valorDe(a, ord.col)
      const vb = valorDe(b, ord.col)
      // vazio ("—", "Em nenhum lugar") sempre no fim, nos dois sentidos
      if (!va !== !vb) return va ? -1 : 1
      return va.localeCompare(vb, 'pt-BR', { numeric: true }) * sinal || a.sortOrder - b.sortOrder
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todos, busca, entidade, ord, fPasta, fTopico, fUso, catalogo.data])

  /**
   * Reordena dentro da MESMA entidade — a ordem é por entidade, então arrastar
   * um campo de Empresa para o meio dos de Pessoa não teria significado.
   */
  const soltarSobre = (alvo: CustomField) => {
    const origemId = arrasto.current?.id ?? null
    const antes = arrasto.current?.antes ?? true
    limparArrasto()
    if (!origemId || origemId === alvo.id) return
    const origem = todos.find((f) => f.id === origemId)
    if (!origem) return
    if (origem.entity !== alvo.entity) {
      toast.push({ tone: 'warning', message: 'A ordem é por entidade — solte entre campos da mesma.' })
      return
    }
    const daEntidade = todos.filter((f) => f.entity === origem.entity).sort((a, b) => a.sortOrder - b.sortOrder)
    const sem = daEntidade.filter((f) => f.id !== origem.id)
    const at = sem.findIndex((f) => f.id === alvo.id)
    // a linha entra exatamente onde o traço apareceu: acima ou abaixo do alvo
    sem.splice(antes ? at : at + 1, 0, origem)
    // grava só quem realmente mudou de posição
    const mudou = sem.map((f, i) => ({ f, i })).filter(({ f, i }) => f.sortOrder !== i)
    if (mudou.length === 0) return
    Promise.all(mudou.map(({ f, i }) => salvar.mutateAsync({ id: f.id, sortOrder: i })))
      .then(() => toast.push({ tone: 'success', message: 'Ordem salva.' }))
      .catch((e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao reordenar.' }))
  }

  const excluir = async (f: CustomField) => {
    let aviso = 'O campo sai do catálogo e de todos os lugares onde foi colocado.'
    try {
      const impacto = await fieldImpact(f.id)
      if (impacto.itemsComValor > 0) {
        aviso = `${impacto.itemsComValor} registro(s) têm valor neste campo. ${aviso}`
      }
    } catch {
      // sem a prévia de impacto ainda dá para decidir; segue com o aviso padrão
    }
    confirm.ask(aviso, () =>
      apagar.mutate(f.id, {
        onSuccess: () => toast.push({ tone: 'success', message: 'Campo excluído.' }),
        onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao excluir.' }),
      }),
    )
  }

  return (
    <div className="lcf">
      {/* o diálogo de confirmação é montado por quem usa o useConfirm */}
      {confirm.element}
      <div className="lcf__cab">
        <div className="lcf__cab-txt">
          <h2 className="lcf__titulo">Todos os campos</h2>
          <p className="lcf__sub">
            {lista.length} de {todos.length} campo(s) do catálogo ·{' '}
            {ord ? (
              <>
                ordenado por {COLUNAS.find((c) => c.col === ord.col)?.label}{' '}
                {ord.dir === 'asc' ? '(crescente)' : '(decrescente)'} ·{' '}
                <button type="button" className="lcf__sub-link" onClick={() => setOrd(null)}>
                  voltar à ordem manual
                </button>
              </>
            ) : (
              'arraste pela alça para mudar a ordem'
            )}
          </p>
        </div>
        <Button variant="secondary" icon="fas fa-arrow-left" onClick={onFechar}>
          Voltar ao construtor
        </Button>
      </div>

      <div className="lcf__filtros">
        <div className="lcf__busca">
          <Input
            value={busca}
            placeholder="Buscar por nome, chave ou tipo…"
            aria-label="Buscar campo"
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        {/* pasta do catálogo · tópico · uso — os três recortes que a lista
            mostra em coluna, agora também como filtro (2026-08-31) */}
        <div className="lcf__sel">
          <Select
            value={fPasta}
            aria-label="Filtrar por pasta"
            onChange={setFPasta}
            options={[
              { value: 'all', label: 'Todas as pastas', icon: 'fas fa-folder-open' },
              { value: 'none', label: 'Sem pasta', icon: 'far fa-folder' },
              ...pastas
                .map((p) => ({ value: p.id, label: rotuloDe(p.labels, 'Pasta'), icon: 'fas fa-folder' }))
                .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
            ]}
          />
        </div>
        <div className="lcf__sel">
          <Select
            value={fTopico}
            aria-label="Filtrar por tópico"
            onChange={setFTopico}
            options={[
              { value: 'all', label: 'Todos os tópicos', icon: 'fas fa-layer-group' },
              { value: 'none', label: 'Sem tópico', icon: 'fas fa-ban' },
              ...topicos
                .map((t) => ({ value: t.id, label: rotuloDe(t.labels, 'Tópico'), icon: 'fas fa-sliders-h' }))
                .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
            ]}
          />
        </div>
        <div className="lcf__sel">
          <Select
            value={fUso}
            aria-label="Filtrar por uso"
            onChange={setFUso}
            options={[
              { value: 'all', label: 'Usado em qualquer lugar', icon: 'fas fa-globe' },
              { value: 'any', label: 'Usado em algum lugar', icon: 'fas fa-check' },
              { value: 'none', label: 'Em nenhum lugar', icon: 'fas fa-times' },
              { value: 'bar', label: 'Barra de propriedades', icon: 'fas fa-list' },
            ]}
          />
        </div>
        <div className="lcf__abas" role="tablist" aria-label="Entidade">
          {[{ value: 'all' as const, label: 'Todas', icon: 'fas fa-layer-group' }, ...ENTIDADES].map((op) => (
            <button
              key={op.value}
              type="button"
              role="tab"
              aria-selected={entidade === op.value}
              className={`lcf__aba${entidade === op.value ? ' lcf__aba--on' : ''}`}
              onClick={() => setEntidade(op.value)}
            >
              <i className={op.icon} aria-hidden />
              {op.label}
            </button>
          ))}
        </div>
      </div>

      {catalogo.isLoading ? (
        <div className="lcf__skel" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="lcf__skel-linha" />
          ))}
        </div>
      ) : lista.length === 0 ? (
        <div className="lcf__vazio">
          <i className="fas fa-inbox" aria-hidden />
          <p>{busca ? 'Nenhum campo encontrado com esse termo.' : 'Nenhum campo no catálogo ainda.'}</p>
          {busca ? (
            <Button variant="secondary" onClick={() => setBusca('')}>
              Limpar a busca
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="lcf__tabela-wrap">
          {/* durante o arraste o realce de passar o mouse sai de cena: com ele,
              a linha acende e apaga junto com o traço e parece que pisca */}
          <table ref={tabelaRef} className="lcf__tabela">
            <thead>
              <tr>
                <th className="lcf__th-alca" aria-label="Ordem" />
                {COLUNAS.map(({ col, label }) => (
                  <th
                    key={col}
                    aria-sort={ord?.col === col ? (ord.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      className={`lcf__ord${ord?.col === col ? ' lcf__ord--on' : ''}`}
                      onClick={() => ordenar(col)}
                      title={
                        ord?.col === col && ord.dir === 'desc'
                          ? 'Clique para voltar à ordem manual'
                          : `Ordenar por ${label}`
                      }
                    >
                      {label}
                      <i
                        className={`fas fa-${
                          ord?.col === col ? (ord.dir === 'asc' ? 'sort-up' : 'sort-down') : 'sort'
                        } lcf__ord-i`}
                        aria-hidden
                      />
                    </button>
                  </th>
                ))}
                <th className="lcf__th-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((f) => {
                const usos = usoDe(f)
                const pasta = pastas.find((p) => p.id === f.catalogGroupId)
                const ent = ENTIDADES.find((e) => e.value === f.entity)
                return (
                  <tr
                    key={f.id}
                    onDragOver={(e) => {
                      const a = arrasto.current
                      if (!a || a.id === f.id) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      // metade de cima = entra ANTES; metade de baixo = DEPOIS
                      const r = e.currentTarget.getBoundingClientRect()
                      const antes = e.clientY < r.top + r.height / 2
                      if (a.alvo === f.id && a.antes === antes) return
                      a.alvo = f.id
                      a.antes = antes
                      marcarDestino(e.currentTarget, antes)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      soltarSobre(f)
                    }}
                  >
                    <td className="lcf__alca">
                      {/* com a lista ordenada por coluna, arrastar não teria
                          onde encaixar: a alça sai de cena até voltar a manual */}
                      <span
                        draggable={!ord}
                        role="button"
                        tabIndex={0}
                        className={ord ? 'lcf__alca--off' : undefined}
                        aria-label={`Mover ${rotuloDe(f.labels, f.key)}`}
                        title={
                          ord
                            ? 'Volte à ordem manual para reordenar arrastando'
                            : 'Arraste para mudar a posição'
                        }
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move'
                          e.dataTransfer.setData('text/plain', f.key)
                          arrasto.current = { id: f.id, alvo: null, antes: true }
                          // a linha inteira é a "sombra" que segue o cursor
                          const linha = e.currentTarget.closest('tr')
                          if (linha) {
                            e.dataTransfer.setDragImage(linha, 24, 18)
                            linha.classList.add('lcf__linha--indo')
                          }
                          tabelaRef.current?.classList.add('lcf__tabela--arrastando')
                        }}
                        onDragEnd={limparArrasto}
                      >
                        <i className="fas fa-grip-vertical" aria-hidden />
                      </span>
                    </td>
                    <td>
                      <button type="button" className="lcf__nome" onClick={() => onEditarCampo(f)}>
                        {rotuloDe(f.labels, f.key)}
                      </button>
                      {f.required ? <Badge tone="warning">Obrigatório</Badge> : null}
                      {!f.defaultVisible ? <Badge tone="neutral">Oculto</Badge> : null}
                    </td>
                    <td>
                      <code className="lcf__chave">{f.key}</code>
                    </td>
                    <td>
                      <span className="lcf__tipo">
                        <i className={TIPOS_CAMPO.find((t) => t.value === f.type)?.icon ?? 'fas fa-font'} aria-hidden />
                        {TIPOS_CAMPO.find((t) => t.value === f.type)?.label ?? f.type}
                      </span>
                    </td>
                    <td>
                      <span className="lcf__ent">
                        <i className={ent?.icon ?? ''} aria-hidden />
                        {ent?.label ?? f.entity}
                      </span>
                    </td>
                    <td>
                      {pasta ? (
                        <span className="lcf__pasta">
                          <i className="fas fa-folder" aria-hidden />
                          {rotuloDe(pasta.labels, 'Pasta')}
                        </span>
                      ) : (
                        <span className="lcf__nada">—</span>
                      )}
                    </td>
                    <td>
                      {usos.length === 0 ? (
                        <span className="lcf__naousado" title="O campo existe no catálogo mas não foi colocado em nenhum lugar">
                          Em nenhum lugar
                        </span>
                      ) : (
                        <span className="lcf__usos">
                          {usos.map((u) => (
                            <span key={u} className="lcf__uso">
                              {u}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td className="lcf__acoes">
                      <button type="button" title="Editar campo" aria-label="Editar campo" onClick={() => onEditarCampo(f)}>
                        <i className="fas fa-pen" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="lcf__excluir"
                        title="Excluir campo"
                        aria-label="Excluir campo"
                        onClick={() => void excluir(f)}
                      >
                        <i className="fas fa-trash" aria-hidden />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
